import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function formatMonth(key) {
  const [y, m] = key.split('-');
  return `${MONTHS[parseInt(m) - 1]} ${y}`;
}

function formatAmount(n) {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);
}

export default function SoloDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [entries,  setEntries]  = useState([]);
  const [summary,  setSummary]  = useState({});
  const [loading,  setLoading]  = useState(true);
  const [filter,   setFilter]   = useState({ type: '', from: '', to: '' });
  const [form,     setForm]     = useState({ date: '', description: '', type: 'income', amount: '', paymentStatus: 'received', notes: '' });
  const [adding,   setAdding]   = useState(false);
  const [formErr,  setFormErr]  = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.type) params.type = filter.type;
      if (filter.from) params.from = filter.from;
      if (filter.to)   params.to   = filter.to;
      const [listRes, sumRes] = await Promise.all([
        api.get('/solo/income', { params }),
        api.get('/solo/income/summary')
      ]);
      setEntries(listRes.data.data || []);
      setSummary(sumRes.data.data || {});
    } catch { /* errors shown via empty state */ }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async id => {
    if (!window.confirm('Delete this entry?')) return;
    await api.delete(`/solo/income/${id}`);
    fetchData();
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setFormErr('');
    if (!form.date)        { setFormErr('Date is required.');        return; }
    if (!form.description) { setFormErr('Description is required.'); return; }
    if (!form.amount || isNaN(form.amount) || Number(form.amount) < 0) { setFormErr('Enter a valid amount.'); return; }
    setAdding(true);
    try {
      await api.post('/solo/income', { ...form, amount: Number(form.amount) });
      setForm({ date: '', description: '', type: 'income', amount: '', paymentStatus: 'received', notes: '' });
      fetchData();
    } catch (err) {
      setFormErr(err.response?.data?.message || 'Failed to add entry.');
    } finally { setAdding(false); }
  };

  if (user?.primaryMode !== 'solo') {
    return <div style={styles.container}><p style={styles.muted}>Solo healer account required.</p></div>;
  }

  const thisMonth = new Date().toISOString().slice(0, 7);
  const thisMonthData = summary[thisMonth] || { income: 0, expense: 0, net: 0 };

  return (
    <div style={styles.container}>
      <h1 style={styles.pageTitle}>Income Tracker</h1>

      {/* Billing & Plan */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Billing & Plan</h2>
        <p style={{ fontSize: 14, color: '#555', marginBottom: 12 }}>
          Manage your subscription, upgrade or cancel your plan.
        </p>
        <button onClick={() => navigate('/solo/billing')} style={styles.billingBtn}>
          Manage Plan →
        </button>
      </div>

      {/* Monthly summary strip */}
      <div style={styles.summaryRow}>
        <div style={styles.summaryCard}>
          <span style={styles.summaryLabel}>Income this month</span>
          <span style={{ ...styles.summaryAmount, color: '#16a34a' }}>{formatAmount(thisMonthData.income)}</span>
        </div>
        <div style={styles.summaryCard}>
          <span style={styles.summaryLabel}>Expenses</span>
          <span style={{ ...styles.summaryAmount, color: '#dc2626' }}>{formatAmount(thisMonthData.expense)}</span>
        </div>
        <div style={styles.summaryCard}>
          <span style={styles.summaryLabel}>Net</span>
          <span style={{ ...styles.summaryAmount, color: thisMonthData.net >= 0 ? '#1a6b5a' : '#dc2626' }}>{formatAmount(thisMonthData.net)}</span>
        </div>
      </div>

      {/* Add entry form */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Add Entry</h2>
        {formErr && <div style={styles.errorBanner}>{formErr}</div>}
        <form onSubmit={handleSubmit} style={styles.addForm} noValidate>
          <div style={styles.formRow}>
            <div style={styles.formField}>
              <label style={styles.label}>Date *</label>
              <input type="date" style={styles.input} value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
            </div>
            <div style={styles.formField}>
              <label style={styles.label}>Type *</label>
              <select style={styles.input} value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                <option value="income">Income</option>
                <option value="expense">Expense</option>
              </select>
            </div>
          </div>
          <label style={styles.label}>Description *</label>
          <input style={styles.input} placeholder="e.g. Session fee — Priya" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          <div style={styles.formRow}>
            <div style={styles.formField}>
              <label style={styles.label}>Amount (₹) *</label>
              <input type="number" min="0" style={styles.input} placeholder="0" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} />
            </div>
            <div style={styles.formField}>
              <label style={styles.label}>Status</label>
              <select style={styles.input} value={form.paymentStatus} onChange={e => setForm(f => ({ ...f, paymentStatus: e.target.value }))}>
                <option value="received">Received</option>
                <option value="pending">Pending</option>
                <option value="waived">Waived</option>
              </select>
            </div>
          </div>
          <label style={styles.label}>Notes (optional)</label>
          <input style={styles.input} placeholder="Optional note" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          <button type="submit" style={styles.addBtn} disabled={adding}>{adding ? 'Saving…' : '+ Add Entry'}</button>
        </form>
      </div>

      {/* Filter + list */}
      <div style={styles.section}>
        <div style={styles.filterRow}>
          <select style={styles.filterInput} value={filter.type} onChange={e => setFilter(f => ({ ...f, type: e.target.value }))}>
            <option value="">All types</option>
            <option value="income">Income</option>
            <option value="expense">Expense</option>
          </select>
          <input type="date" style={styles.filterInput} value={filter.from} onChange={e => setFilter(f => ({ ...f, from: e.target.value }))} />
          <input type="date" style={styles.filterInput} value={filter.to}   onChange={e => setFilter(f => ({ ...f, to: e.target.value }))} />
        </div>

        {loading ? <p style={styles.muted}>Loading…</p> : entries.length === 0 ? (
          <p style={styles.muted}>No entries found. Add your first entry above.</p>
        ) : (
          <div style={styles.list}>
            {entries.map(entry => (
              <div key={entry._id} style={styles.entryRow}>
                <div style={styles.entryLeft}>
                  <span style={{ ...styles.typeDot, backgroundColor: entry.type === 'income' ? '#16a34a' : '#dc2626' }} />
                  <div>
                    <div style={styles.entryDesc}>{entry.description}</div>
                    <div style={styles.entryMeta}>{new Date(entry.date).toLocaleDateString('en-IN')} · {entry.paymentStatus}</div>
                  </div>
                </div>
                <div style={styles.entryRight}>
                  <span style={{ ...styles.entryAmount, color: entry.type === 'income' ? '#16a34a' : '#dc2626' }}>
                    {entry.type === 'expense' ? '−' : '+'}{formatAmount(entry.amount)}
                  </span>
                  <button style={styles.deleteBtn} onClick={() => handleDelete(entry._id)}>✕</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Historical monthly breakdown */}
        {Object.keys(summary).length > 0 && (
          <div style={{ marginTop: 24 }}>
            <h3 style={styles.sectionTitle}>Monthly breakdown</h3>
            {Object.entries(summary).map(([key, val]) => (
              <div key={key} style={styles.monthRow}>
                <span style={styles.monthLabel}>{formatMonth(key)}</span>
                <span style={{ color: '#16a34a', minWidth: 90, textAlign: 'right' }}>{formatAmount(val.income)}</span>
                <span style={{ color: '#dc2626', minWidth: 90, textAlign: 'right' }}>{formatAmount(val.expense)}</span>
                <span style={{ color: val.net >= 0 ? '#1a6b5a' : '#dc2626', minWidth: 90, textAlign: 'right', fontWeight: 600 }}>{formatAmount(val.net)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container:     { maxWidth: 680, margin: '0 auto', padding: '24px 16px' },
  pageTitle:     { fontSize: 22, fontWeight: 800, color: '#1a3d2b', marginBottom: 20 },
  summaryRow:    { display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' },
  summaryCard:   { flex: 1, minWidth: 120, backgroundColor: '#f0faf6', borderRadius: 12, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 4 },
  summaryLabel:  { fontSize: 12, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase' },
  summaryAmount: { fontSize: 20, fontWeight: 800 },
  section:       { backgroundColor: '#fff', borderRadius: 12, padding: 20, marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  sectionTitle:  { fontSize: 16, fontWeight: 700, color: '#1a3d2b', marginBottom: 14 },
  addForm:       { display: 'flex', flexDirection: 'column', gap: 8 },
  formRow:       { display: 'flex', gap: 12, flexWrap: 'wrap' },
  formField:     { flex: 1, minWidth: 130 },
  label:         { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4, marginTop: 6 },
  input:         { width: '100%', padding: '10px 12px', fontSize: 15, border: '1.5px solid #d1d5db', borderRadius: 8, boxSizing: 'border-box', backgroundColor: '#fff' },
  addBtn:        { marginTop: 10, padding: '12px 0', backgroundColor: '#1a6b5a', color: '#fff', fontSize: 15, fontWeight: 700, border: 'none', borderRadius: 8, cursor: 'pointer', minHeight: 44 },
  filterRow:     { display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' },
  filterInput:   { flex: 1, minWidth: 110, padding: '9px 12px', fontSize: 14, border: '1.5px solid #e5e7eb', borderRadius: 8, backgroundColor: '#fff' },
  list:          { display: 'flex', flexDirection: 'column', gap: 10 },
  entryRow:      { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f3f4f6' },
  entryLeft:     { display: 'flex', alignItems: 'center', gap: 12 },
  typeDot:       { width: 10, height: 10, borderRadius: 5, flexShrink: 0 },
  entryDesc:     { fontSize: 15, color: '#111827', fontWeight: 500 },
  entryMeta:     { fontSize: 12, color: '#9ca3af', marginTop: 2 },
  entryRight:    { display: 'flex', alignItems: 'center', gap: 12 },
  entryAmount:   { fontSize: 15, fontWeight: 700, minWidth: 80, textAlign: 'right' },
  deleteBtn:     { background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 14, padding: '4px 8px', minHeight: 44, minWidth: 44 },
  monthRow:      { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid #f3f4f6', fontSize: 14 },
  monthLabel:    { flex: 1, color: '#374151', fontWeight: 500 },
  errorBanner:   { backgroundColor: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626', borderRadius: 8, padding: '10px 14px', fontSize: 14, marginBottom: 10 },
  muted:         { color: '#9ca3af', fontSize: 14, textAlign: 'center', padding: '20px 0' },
  billingBtn:    { background: '#4a8f82', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
};
